import React from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Button, Tag } from "@wfp/ui";

import { resetStore } from "data-handler/ducks/stores";
import { getCurrentUser } from "data-handler/ducks/auth";
import { getIsAdmin } from "helpers/users";

import "./_syncError.scss";

const ERROR_CODE_LABELS = {
  SERIALIZER_INVALID: (
    <FormattedMessage
      id="SyncError.code.SERIALIZER_INVALID"
      defaultMessage="Malformed Data"
    />
  ),
  SERVER_ERROR: (
    <FormattedMessage
      id="SyncError.code.SERVER_ERROR"
      defaultMessage="Server Error"
    />
  ),
  REPORT_UNEDITABLE: (
    <FormattedMessage
      id="SyncError.code.REPORT_UNEDITABLE"
      defaultMessage="Report Closed"
    />
  ),
  SYNC_DOES_NOT_EXIST: (
    <FormattedMessage
      id="SyncError.code.SYNC_DOES_NOT_EXIST"
      defaultMessage="Removed on the server"
    />
  ),
};

const ERROR_CODE_HELP = {
  SERIALIZER_INVALID: (
    <FormattedMessage
      id="SyncError.help.SERIALIZER_INVALID"
      defaultMessage="Try editing again before syncing"
    />
  ),
  SERVER_ERROR: (
    <FormattedMessage
      id="SyncError.help.SERVER_ERROR"
      defaultMessage="Try syncing again"
    />
  ),
  REPORT_UNEDITABLE: (
    <FormattedMessage
      id="SyncError.help.REPORT_UNEDITABLE"
      defaultMessage="You must reopen the report to add or edit data"
    />
  ),
  SYNC_DOES_NOT_EXIST: (
    <FormattedMessage
      id="SyncError.help.SYNC_DOES_NOT_EXIST"
      defaultMessage="This entry was merged or removed on the server. Drop it on this device."
    />
  ),
};

const extractErrorMessages = (errorDetails) => {
  if (!errorDetails) return [];

  const messages = [];

  // Handle nested object format: {store_event: {stock_events: [...]}}
  const traverse = (value) => {
    if (typeof value === "string") {
      messages.push(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(traverse);
      return;
    }

    if (typeof value === "object" && value !== null) {
      Object.values(value).forEach(traverse);
    }
  };

  traverse(errorDetails);

  return messages;
};

export default function SyncError({
  syncable: { client_id, id, error, errorDetails },
}) {
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);

  if (error === undefined) {
    return null;
  }

  const isNew = id === undefined;
  const isAdmin = getIsAdmin(currentUser);

  return (
    <div>
      <Tag type="error">{ERROR_CODE_LABELS[error]}</Tag>
      <div style={{ margin: "0.25rem" }}>{ERROR_CODE_HELP[error]}</div>

      {/* Button for deleting the new sync or undoing its newest edits */}
      <Button kind="ghost" onClick={() => dispatch(resetStore(client_id))}>
        {isNew ? (
          <FormattedMessage
            id="SyncError.dropSyncable"
            defaultMessage="Drop Entry"
          />
        ) : (
          <FormattedMessage
            id="SyncError.undoModifications"
            defaultMessage="Undo Modifications"
          />
        )}
      </Button>

      {errorDetails && (
        <div className="sync-error__details">
          {extractErrorMessages(errorDetails).map((message, index) => (
            <div key={index} className="sync-error__message">
              {message}
            </div>
          ))}
        </div>
      )}

      {isAdmin && errorDetails && (
        <details className="sync-error__debug">
          <summary className="sync-error__debug-summary">
            Raw Error Data
          </summary>
          <pre className="sync-error__debug-content">
            {JSON.stringify(errorDetails, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
